/* Pure helpers behind RouteErrorBoundary (B6 audit, 2026-09-04): the page
 * name the fallback states, the support line's error text, and the date
 * stamp — kept free of React so `npm run test:unit` can pin them. */

/** The rail label of the page a pathname lands on — what the officer
 * clicked, in the officer's words. Longest prefix wins; unknown paths
 * fall back to the last path segment, humanised. */
const PAGE_NAMES: ReadonlyArray<readonly [string, string]> = [
  ['/v2/admin/policy', 'Policy'],
  ['/v2/admin/users', 'Users'],
  ['/v2/admin/logins', 'Logins'],
  ['/v2/admin/health', 'System health'],
  ['/v2/admin/eqs', 'Extraction quality'],
  ['/v2/admin/prompt-ops', 'Prompt optimization'],
  ['/v2/admin', 'Admin'],
  ['/v2/workbench/reconciliation', 'Reconciliation'],
  ['/v2/workbench', 'Workbench'],
  ['/v2/tasks', 'Tasks'],
  ['/v2/funds', 'Funds'],
  ['/v2/traces', 'Traces'],
  ['/v2/documents', 'Traces'],
  ['/v2/manual', 'Manual entry'],
  ['/v2/proposals', 'Proposals'],
  ['/v2/ops', 'Other operations'],
  ['/v2', 'Today'],
  ['/review', 'Chain review'],
  ['/orphans', 'Parked documents'],
  ['/login', 'Log in'],
]

export function pageNameFor(pathname: string): string {
  const path = pathname.replace(/\/+$/, '') || '/'
  for (const [prefix, name] of PAGE_NAMES) {
    // Today is the index of /v2, not the parent of every v2 page.
    if (prefix === '/v2') {
      if (path === prefix) return name
      continue
    }
    if (path === prefix || path.startsWith(prefix + '/')) return name
  }
  const last = path.split('/').filter(Boolean).pop()
  if (!last) return 'Today'
  const words = last.replace(/[-_]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** The one line support needs: the error's own message (name-prefixed when
 * the name carries meaning), never a stack. Non-Error throws are stringified. */
export function errorText(err: unknown): string {
  if (err instanceof Error) {
    const msg = err.message?.trim() || '(no message)'
    return err.name && err.name !== 'Error' ? `${err.name}: ${msg}` : msg
  }
  if (typeof err === 'string') return err.trim() || '(no message)'
  try {
    const s = JSON.stringify(err)
    return s === undefined ? String(err) : s
  } catch {
    return String(err)
  }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "16 Feb 2026, 14:02" — the house date shape, with the local time. */
export function stampDate(d: Date): string {
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}, ${hh}:${mm}`
}

/** "16 Feb 2026, 14:02:37" — the same stamp with seconds, for a failed
 * read the officer may report minutes later (ErrorState). */
export function stampDateTime(d: Date): string {
  const ss = String(d.getSeconds()).padStart(2, '0')
  return `${stampDate(d)}:${ss}`
}
